/**
 * Voucher type master parsing and normalization utilities.
 * Pure functions extracted from TDSImport.jsx for maintainability.
 */
import * as XLSX from "xlsx";

const PATTERNS = [
  { pattern: /^purchase/i, parent: "purchase" },
  { pattern: /^journal/i, parent: "journal" },
  { pattern: /^expense\s*jv/i, parent: "journal" },
  { pattern: /^bank\s*payment/i, parent: "payment" },
  { pattern: /^cash\s*payment/i, parent: "payment" },
  { pattern: /^payment/i, parent: "payment" },
  { pattern: /^bank\s*receipt/i, parent: "receipt" },
  { pattern: /^cash\s*receipt/i, parent: "receipt" },
  { pattern: /^receipt/i, parent: "receipt" },
  { pattern: /^sales/i, parent: "sales" },
  { pattern: /^services/i, parent: "sales" },
  { pattern: /^contra/i, parent: "contra" },
  { pattern: /^credit\s*note/i, parent: "creditnote" },
  { pattern: /^debit\s*note/i, parent: "debitnote" },
];

export const detectParentVoucherType = (vchName) => {
  if (!vchName) return null;
  const name = vchName.trim().toLowerCase();
  for (const { pattern, parent } of PATTERNS) {
    if (pattern.test(name)) return parent;
  }
  return null;
};

const TYPE_MAP = {
  jrnl: "journal", journal: "journal",
  purc: "purchase", purchase: "purchase", purchases: "purchase",
  pymt: "payment", payment: "payment",
  rcpt: "receipt", receipt: "receipt",
  sales: "sales", sale: "sales",
  contra: "contra", cntr: "contra",
  "debit note": "debitnote", debitnote: "debitnote", "dbt nt": "debitnote", "d/note": "debitnote",
  "credit note": "creditnote", creditnote: "creditnote", "cdt nt": "creditnote", "c/note": "creditnote",
  cn: "creditnote",
};

export const normalizeVoucherType = (vchType, masterMappings = null) => {
  if (!vchType) return "";
  const type = vchType.trim().toLowerCase();

  if (masterMappings && masterMappings[type]) return masterMappings[type];
  if (TYPE_MAP[type]) return TYPE_MAP[type];

  const basePart = type.split(" - ")[0].trim();
  if (TYPE_MAP[basePart]) return TYPE_MAP[basePart];

  const detected = detectParentVoucherType(vchType);
  if (detected) return detected;

  if (type.startsWith("purchase")) return "purchase";
  if (type.startsWith("journal")) return "journal";
  if (type.startsWith("payment")) return "payment";
  if (type.startsWith("receipt")) return "receipt";
  if (type.startsWith("sale")) return "sales";

  return type;
};

const isValidVoucherName = (vchName) =>
  vchName &&
  !vchName.includes("CIN:") &&
  !vchName.includes("List of") &&
  !vchName.includes("Voucher Type(s)") &&
  !vchName.match(/^\d+-[A-Za-z]+-\d+/) &&
  !vchName.match(/^[A-Za-z]+ [A-Za-z]+ Pvt/);

export const parseVoucherTypeMaster = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        const mappings = {};
        const voucherTypes = [];

        jsonData.forEach((row) => {
          if (!row || !row[0]) return;
          const vchName = String(row[0]).trim();
          if (!isValidVoucherName(vchName)) return;
          const parentType = detectParentVoucherType(vchName);
          if (parentType) {
            mappings[vchName.toLowerCase()] = parentType;
            voucherTypes.push({ name: vchName, parent: parentType });
          }
        });

        resolve({ mappings, voucherTypes, filename: file.name });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

export const parseVoucherTypeMasterXML = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let text = e.target.result;

        if (text.charCodeAt(0) === 0xfffe || text.charCodeAt(0) === 0xfeff) {
          const decoder = new TextDecoder("utf-16le");
          text = decoder.decode(e.target.result);
        }

        text = text.replace(/^\uFEFF/, "").replace(/^\xEF\xBB\xBF/, "");

        const mappings = {};
        const voucherTypes = [];
        const vchTypeBlockRegex = /<VOUCHERTYPE\s+NAME="([^"]+)"[^>]*>[\s\S]*?<PARENT>([^<]*)<\/PARENT>[\s\S]*?<\/VOUCHERTYPE>/gi;
        let match;

        while ((match = vchTypeBlockRegex.exec(text)) !== null) {
          const vchName = match[1].trim();
          const parentName = match[2].trim();
          if (vchName && parentName) {
            const normalizedParent = detectParentVoucherType(parentName) || parentName.toLowerCase();
            mappings[vchName.toLowerCase()] = normalizedParent;
            voucherTypes.push({ name: vchName, parent: normalizedParent, tallyParent: parentName });
          }
        }

        resolve({ mappings, voucherTypes, filename: file.name });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
